// /routes/:id — run-the-route screen: map + list of this route's leads, departure
// /destination controls, Generate Route (optimize + persist order), Add Leads, and
// per-stop directions / SMS / call / view actions.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaArrowLeft, FaRoute, FaEdit, FaTrash, FaPlus, FaMap, FaList, FaMagic, FaSpinner,
  FaExternalLinkAlt, FaCalendarDay, FaClock, FaRoad,
} from 'react-icons/fa';
import DashboardLayout from '../../../layouts/DashboardLayout';
import Modal from '../../../components/shared/Modal';
import ModalNotification from '../../../components/shared/ModalNotification';
import CallModal from '../../../components/shared/CallModal';
import SmsModal from '../../leads/SmsModal';
import { useAuth } from '../../auth/AuthContext';
import useGoogleMapsLoader from '../../leads/route-planner/useGoogleMapsLoader';
import useSavedRoute from '../useSavedRoute';
import RouteMap from '../components/RouteMap';
import RouteStopList from '../components/RouteStopList';
import RouteMaterialsSummary from '../components/RouteMaterialsSummary';
import useRouteMaterials from '../useRouteMaterials';
import DepartureDestination from '../components/DepartureDestination';
import RouteFormModal from '../components/RouteFormModal';
import AddLeadsModal from '../components/AddLeadsModal';
import {
  updateRoute, deleteRoute, addLeadsToRoute, removeStopFromRoute,
} from '../routeService';
import { formatRouteDate, routeDateRelative, formatStopAddress } from '../utils';

const Page = styled.div`max-width: 1050px; margin: 0 auto;`;
const TopBar = styled.div`margin-bottom: ${p => p.theme.spacing(2)};`;
const BackBtn = styled.button`
  display: inline-flex; align-items: center; gap: 7px; background: transparent;
  border: 1px solid ${p => p.theme.colors.inputGray}44; border-radius: 999px;
  color: ${p => p.theme.colors.textSecondary}; padding: 8px 14px; cursor: pointer; font-size: 0.84rem;
  &:hover { color: ${p => p.theme.colors.primary}; border-color: ${p => p.theme.colors.primary}; }
`;
const Card = styled.div`
  background: ${p => p.theme.colors.surface}; border-radius: 16px; border: 1px solid ${p => p.theme.colors.inputGray}22;
  padding: ${p => p.theme.spacing(2.5)}; margin-bottom: ${p => p.theme.spacing(2)};
  @media (max-width: 480px) { padding: ${p => p.theme.spacing(2)}; }
`;
const HeadRow = styled.div`
  display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-wrap: wrap;
  .name { font-size: 1.35rem; font-weight: 800; color: ${p => p.theme.colors.textPrimary}; }
  .date { display: inline-flex; align-items: center; gap: 7px; margin-top: 6px; color: ${p => p.theme.colors.primary}; font-weight: 600; font-size: 0.9rem; }
`;
const IconBtns = styled.div`display: flex; gap: 8px;`;
const SmallBtn = styled.button`
  display: inline-flex; align-items: center; gap: 6px; min-height: 40px; padding: 0 13px; border-radius: 10px; cursor: pointer;
  border: 1px solid ${p => (p.$danger ? p.theme.colors.error : p.theme.colors.inputGray + '44')};
  background: ${p => p.$primary ? p.theme.colors.primary : 'transparent'};
  color: ${p => p.$primary ? '#fff' : p.$danger ? p.theme.colors.error : p.theme.colors.textSecondary};
  font-size: 0.84rem; font-weight: 600;
  &:hover { border-color: ${p => p.$danger ? p.theme.colors.error : p.theme.colors.primary}; }
  &:disabled { opacity: 0.55; cursor: default; }
`;
const GenRow = styled.div`display: flex; gap: 10px; flex-wrap: wrap; align-items: center; margin-top: 14px;`;
const GenBtn = styled.button`
  display: inline-flex; align-items: center; gap: 9px; min-height: 46px; padding: 0 20px; border: none; border-radius: 12px;
  background: linear-gradient(135deg, ${p => p.theme.colors.primary}, #e07d00); color: #fff; font-weight: 800; font-size: 0.95rem; cursor: pointer;
  box-shadow: 0 4px 14px ${p => p.theme.colors.primary}55;
  &:hover { opacity: 0.95; } &:disabled { opacity: 0.5; cursor: default; box-shadow: none; }
  svg.spin { animation: spin 1s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }
`;
const Info = styled.div`
  display: inline-flex; align-items: center; gap: 14px; color: ${p => p.theme.colors.textSecondary}; font-size: 0.86rem;
  span { display: inline-flex; align-items: center; gap: 6px; } svg { color: ${p => p.theme.colors.primary}; }
`;
const Toggle = styled.div`
  display: inline-flex; background: ${p => p.theme.colors.background}; border-radius: 999px; padding: 4px; gap: 4px; margin-bottom: ${p => p.theme.spacing(2)};
`;
const ToggleBtn = styled.button`
  display: inline-flex; align-items: center; gap: 7px; padding: 8px 18px; border-radius: 999px; border: none; cursor: pointer;
  font-size: 0.85rem; font-weight: 700;
  background: ${p => p.$active ? p.theme.colors.primary : 'transparent'}; color: ${p => p.$active ? '#fff' : p.theme.colors.textSecondary};
`;
const MapWrap = styled.div`height: 62vh; min-height: 420px; border-radius: 14px; overflow: hidden;`;
const ErrText = styled.div`color: ${p => p.theme.colors.error}; font-size: 0.85rem; margin-top: 10px;`;
const Empty = styled.div`text-align: center; padding: 50px 20px; color: ${p => p.theme.colors.textSecondary};`;
const Loader = styled.div`display: flex; justify-content: center; padding: 100px; color: ${p => p.theme.colors.primary}; svg { font-size: 32px; animation: spin 1s linear infinite; } @keyframes spin { to { transform: rotate(360deg);} }`;

const RouteDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isLoaded: mapsLoaded } = useGoogleMapsLoader();

  const r = useSavedRoute(id, mapsLoaded);
  const materials = useRouteMaterials(r.leads || []);
  const [view, setView] = useState('map');
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [smsLead, setSmsLead] = useState(null);
  const [callLead, setCallLead] = useState(null);
  const [feedback, setFeedback] = useState({ show: false, message: '', success: true });
  const notify = (message, success = true) => setFeedback({ show: true, message, success });

  // Draw the polyline for the saved order once maps + data are ready (and after
  // the lead set / start point changes). Guarded so it doesn't loop.
  const sigRef = useRef('');
  useEffect(() => {
    if (!mapsLoaded || r.loading) return;
    // Keyed by stopId — lead ids no longer identify a stop uniquely.
    const sig = JSON.stringify([r.departure, r.leads.map(s => s.stopId)]);
    if (sig === sigRef.current) return;
    sigRef.current = sig;
    if (r.departure && r.leads.length) r.drawSavedOrder();
  }, [mapsLoaded, r.loading, r.departure, r.leads, r.drawSavedOrder]);

  const onEndpointsChange = useCallback((patch) => {
    if (patch.departure !== undefined) r.setDeparture(patch.departure);
    if (patch.destination !== undefined) r.setDestination(patch.destination);
    if (patch.isRoundTrip !== undefined) r.setIsRoundTrip(patch.isRoundTrip);
    // Persist to backend (best effort).
    const dep = patch.departure ?? r.departure;
    const dest = patch.destination ?? r.destination;
    const round = patch.isRoundTrip ?? r.isRoundTrip;
    r.persistEndpoints({
      is_round_trip: round,
      departure_label: dep?.label ?? null, departure_lat: dep?.lat ?? null, departure_lng: dep?.lng ?? null,
      destination_label: dest?.label ?? null, destination_lat: dest?.lat ?? null, destination_lng: dest?.lng ?? null,
    });
  }, [r]);

  const handleEdit = async ({ name, date }) => {
    await updateRoute(id, { name, date });
    setEditOpen(false);
    r.reload();
  };
  const handleDelete = async () => {
    try { await deleteRoute(id); navigate('/routes'); }
    catch { setDeleteOpen(false); notify('Failed to delete route', false); }
  };
  const handleAddLeads = async (leadIds) => {
    setAddOpen(false);
    if (!leadIds.length) return;
    try { await addLeadsToRoute(id, leadIds); notify(`${leadIds.length} lead(s) added`); r.reload(); }
    catch { notify('Failed to add leads', false); }
  };
  // Removes one stop, not every stop belonging to the lead. Memoized because
  // handleAction depends on it (the map's InfoWindow can trigger a removal too).
  const reloadRoute = r.reload;   // stable useCallback from useSavedRoute
  const handleRemove = useCallback(async (stopId) => {
    try { await removeStopFromRoute(id, stopId); notify('Stop removed'); reloadRoute(); }
    catch { notify('Failed to remove stop', false); }
  }, [id, reloadRoute]);

  const openDirections = (stop) => {
    const dest = (stop.latitude && stop.longitude)
      ? `${stop.latitude},${stop.longitude}`
      : formatStopAddress(stop);
    if (!dest) return;
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}&travelmode=driving`, '_blank', 'noopener');
  };

  const handleAction = useCallback((action, stopId) => {
    // Look the stop up by its own id: `find(l => l.id === leadId)` would always
    // return the first stop of a lead that has two.
    const stop = r.leads.find(s => s.stopId === stopId);
    if (!stop) return;
    if (action === 'view') window.open(`/leads/view/${stop.leadId}`, '_blank', 'noopener');
    else if (action === 'directions') openDirections(stop);
    else if (action === 'sms') setSmsLead(stop);
    else if (action === 'call') setCallLead(stop);
    // Parity with the list view's trash button — removes this stop only.
    else if (action === 'remove') handleRemove(stop.stopId);
  }, [r.leads, handleRemove]);

  if (r.loading) return <DashboardLayout><Loader><FaSpinner /></Loader></DashboardLayout>;
  if (r.error || !r.route) {
    return (
      <DashboardLayout>
        <Page>
          <TopBar><BackBtn onClick={() => navigate('/routes')}><FaArrowLeft /> Back</BackBtn></TopBar>
          <Card><p style={{ color: '#ccc' }}>{r.error || 'Route not found.'}</p></Card>
        </Page>
      </DashboardLayout>
    );
  }

  const canGenerate = !!r.departure && r.leads.some(l => l.latitude && l.longitude) && !r.isOptimizing;

  return (
    <DashboardLayout>
      <Page>
        <TopBar><BackBtn onClick={() => navigate('/routes')}><FaArrowLeft /> Back to routes</BackBtn></TopBar>

        <Card>
          <HeadRow>
            <div>
              <div className="name"><FaRoute style={{ color: '#F69620', marginRight: 8 }} />{r.route.name}</div>
              <div className="date"><FaCalendarDay size={12} /> {r.route.date ? `${routeDateRelative(r.route.date)} · ${formatRouteDate(r.route.date)}` : 'No date set'}</div>
            </div>
            <IconBtns>
              <SmallBtn onClick={() => setEditOpen(true)}><FaEdit /> Edit</SmallBtn>
              <SmallBtn $danger onClick={() => setDeleteOpen(true)}><FaTrash /></SmallBtn>
            </IconBtns>
          </HeadRow>

          <div style={{ marginTop: 18 }}>
            <DepartureDestination
              departure={r.departure}
              destination={r.destination}
              isRoundTrip={r.isRoundTrip}
              onChange={onEndpointsChange}
            />
          </div>

          <GenRow>
            <GenBtn onClick={r.generate} disabled={!canGenerate}>
              {r.isOptimizing ? <FaSpinner className="spin" /> : <FaMagic />} Generate Route
            </GenBtn>
            <SmallBtn onClick={() => setAddOpen(true)}><FaPlus /> Add Leads</SmallBtn>
            {r.googleMapsUrl && (
              <SmallBtn as="a" href={r.googleMapsUrl} target="_blank" rel="noopener noreferrer"><FaExternalLinkAlt /> Open in Google Maps</SmallBtn>
            )}
            {r.routeInfo && (
              <Info>
                <span><FaRoad /> {r.routeInfo.totalDistance}</span>
                <span><FaClock /> {r.routeInfo.totalDuration}</span>
              </Info>
            )}
          </GenRow>
          {r.generateError && <ErrText>{r.generateError}</ErrText>}

          <RouteMaterialsSummary materials={materials} />
        </Card>

        <Toggle>
          <ToggleBtn $active={view === 'map'} onClick={() => setView('map')}><FaMap /> Map</ToggleBtn>
          <ToggleBtn $active={view === 'list'} onClick={() => setView('list')}><FaList /> List</ToggleBtn>
        </Toggle>

        {r.leads.length === 0 ? (
          <Card><Empty>No leads in this route yet. Tap <strong style={{ color: '#F69620' }}>Add Leads</strong> to build your visit list.</Empty></Card>
        ) : view === 'map' ? (
          <MapWrap>
            <RouteMap
              isGoogleLoaded={mapsLoaded}
              departure={r.departure}
              leads={r.leads}
              routeResponse={r.routeResponse}
              onAction={handleAction}
            />
          </MapWrap>
        ) : (
          <RouteStopList
            leads={r.leads}
            materials={materials}
            legs={r.routeInfo?.legs}
            onAction={handleAction}
            onRemove={handleRemove}
          />
        )}
      </Page>

      <RouteFormModal isOpen={editOpen} route={r.route} onClose={() => setEditOpen(false)} onSave={handleEdit} />
      <AddLeadsModal isOpen={addOpen} existingIds={r.leads.filter(s => s.isPrimaryAddress).map(s => s.leadId)} onClose={() => setAddOpen(false)} onAdd={handleAddLeads} />

      {smsLead && (
        <SmsModal isOpen={!!smsLead} onClose={() => setSmsLead(null)} lead={{ id: smsLead.leadId, name: smsLead.name, phone: smsLead.phone }} userId={user?.id} />
      )}
      {callLead && (
        <CallModal isOpen={!!callLead} onClose={() => setCallLead(null)} customerPhone={callLead.phone} customerName={callLead.name} leadId={callLead.leadId} />
      )}

      <Modal title="Delete Route" isOpen={deleteOpen} onCancel={() => setDeleteOpen(false)} onConfirm={handleDelete}>
        <p style={{ color: '#ccc', lineHeight: 1.6 }}>Delete <strong style={{ color: '#fff' }}>{r.route.name}</strong>? The leads stay in your CRM; only this route is removed.</p>
      </Modal>

      <ModalNotification
        isOpen={feedback.show}
        message={feedback.message}
        success={feedback.success}
        onComplete={() => setFeedback(prev => ({ ...prev, show: false }))}
      />
    </DashboardLayout>
  );
};

export default RouteDetailPage;
